import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, InputNumber, Tabs, Tag, Popconfirm, message } from "antd";
import { ArrowLeft, Plus, Trash2, Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  useSupplierList, useSupplierCreate, useSupplierUpdate, useSupplierDelete,
  useCategoryList, useCategoryCreate, useCategoryDelete,
  usePartList, usePartCreate, usePartUpdate, usePartDelete,
  useLocationList, useLocationCreate, useLocationDelete,
  useStockLevels, useStockMovements,
} from "../../../api-hooks/inventory";

export default function InventoryPage() {
  const { companyId: cParam } = useParams<{ companyId: string }>();
  const cid = Number(cParam);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();

  // ── Suppliers ────────────────────────────────────────────────────────────
  const { data: suppliers = [], isLoading: supLoading } = useSupplierList(cid);
  const [supModal, setSupModal] = useState<{ open: boolean; record: any | null }>({ open: false, record: null });
  const [supForm] = Form.useForm();
  const createSup = useSupplierCreate(cid, { onSuccess: () => { msgApi.success("Supplier saved."); setSupModal({ open: false, record: null }); supForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const updateSup = useSupplierUpdate(cid, { onSuccess: () => { msgApi.success("Supplier updated."); setSupModal({ open: false, record: null }); supForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const deleteSup = useSupplierDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e) => msgApi.error(e?.message || "Error") });

  // ── Parts ─────────────────────────────────────────────────────────────────
  const { data: partsResult } = usePartList(cid);
  const parts: any[] = partsResult?.data ?? [];
  const { data: categories = [] } = useCategoryList(cid);
  const [partModal, setPartModal] = useState<{ open: boolean; record: any | null }>({ open: false, record: null });
  const [partForm] = Form.useForm();
  const createPart = usePartCreate(cid, { onSuccess: () => { msgApi.success("Part saved."); setPartModal({ open: false, record: null }); partForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const updatePart = usePartUpdate(cid, { onSuccess: () => { msgApi.success("Part updated."); setPartModal({ open: false, record: null }); partForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const deletePart = usePartDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e) => msgApi.error(e?.message || "Error") });
  const createCat = useCategoryCreate(cid, { onSuccess: () => msgApi.success("Category created.") });
  const deleteCat = useCategoryDelete(cid, { onSuccess: () => msgApi.success("Category deleted.") });

  // ── Stock Locations ────────────────────────────────────────────────────────
  const { data: locations = [] } = useLocationList(cid);
  const [locModal, setLocModal] = useState(false);
  const [locForm] = Form.useForm();
  const createLoc = useLocationCreate(cid, { onSuccess: () => { msgApi.success("Location created."); setLocModal(false); locForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const deleteLoc = useLocationDelete(cid, { onSuccess: () => msgApi.success("Deleted.") });

  // ── Stock Levels ───────────────────────────────────────────────────────────
  const { data: levels = [] } = useStockLevels(cid);
  const { data: movements = [] } = useStockMovements(cid);

  const supplierCols = [
    { title: "Name", dataIndex: "name", key: "name", render: (v: string) => <span className="font-medium">{v}</span> },
    { title: "Contact", dataIndex: "contact_name", key: "contact_name", render: (v: string) => v || "—" },
    { title: "Email", dataIndex: "email", key: "email", render: (v: string) => v || "—" },
    { title: "Phone", dataIndex: "phone", key: "phone", render: (v: string) => v || "—" },
    { title: "City", dataIndex: "city", key: "city", render: (v: string) => v || "—" },
    { title: "Terms", dataIndex: "payment_terms_days", key: "payment_terms_days", render: (v: number) => v ? `${v} days` : "—" },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v: number) => <Tag color={v ? "green" : "red"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "", key: "actions", dataIndex: "id", render: (_: any, r: any) => (
        <div className="flex gap-2">
          <Button size="small" icon={<Pencil size={13} />} onClick={() => { setSupModal({ open: true, record: r }); supForm.setFieldsValue(r); }} />
          <Popconfirm title="Delete supplier?" onConfirm={() => deleteSup.mutate(r.id)}><Button size="small" danger icon={<Trash2 size={13} />} /></Popconfirm>
        </div>
      ),
    },
  ];

  const partCols = [
    { title: "SKU", dataIndex: "sku", key: "sku", render: (v: string) => <span className="font-mono text-xs">{v}</span> },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Category", dataIndex: "category_name", key: "category_name", render: (v: string) => v || "—" },
    { title: "Unit", dataIndex: "unit", key: "unit" },
    { title: "Cost", dataIndex: "unit_cost", key: "unit_cost", render: (v: number) => v !== undefined ? `£${Number(v).toFixed(2)}` : "—" },
    { title: "Price", dataIndex: "unit_price", key: "unit_price", render: (v: number) => v !== undefined ? `£${Number(v).toFixed(2)}` : "—" },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v: number) => <Tag color={v ? "green" : "red"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "", key: "actions", dataIndex: "id", render: (_: any, r: any) => (
        <div className="flex gap-2">
          <Button size="small" icon={<Pencil size={13} />} onClick={() => { setPartModal({ open: true, record: r }); partForm.setFieldsValue(r); }} />
          <Popconfirm title="Delete part?" onConfirm={() => deletePart.mutate(r.id)}><Button size="small" danger icon={<Trash2 size={13} />} /></Popconfirm>
        </div>
      ),
    },
  ];

  const locationCols = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Type", dataIndex: "location_type", key: "location_type", render: (v: string) => <Tag>{v}</Tag> },
    { title: "Address", dataIndex: "address_line1", key: "address_line1", render: (v: string) => v || "—" },
    { title: "Postcode", dataIndex: "postcode", key: "postcode", render: (v: string) => v || "—" },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v: number) => <Tag color={v ? "green" : "red"}>{v ? "Active" : "Inactive"}</Tag> },
    { title: "", key: "actions", dataIndex: "id", render: (_: any, r: any) => <Popconfirm title="Delete location?" onConfirm={() => deleteLoc.mutate(r.id)}><Button size="small" danger icon={<Trash2 size={13} />} /></Popconfirm> },
  ];

  const levelCols = [
    { title: "Part", dataIndex: "part_name", key: "part_name" },
    { title: "SKU", dataIndex: "sku", key: "sku", render: (v: string) => <span className="font-mono text-xs">{v}</span> },
    { title: "Location", dataIndex: "location_name", key: "location_name" },
    { title: "Qty", dataIndex: "quantity", key: "quantity", render: (v: number) => <span className="font-semibold">{Number(v).toFixed(0)}</span> },
    { title: "Reserved", dataIndex: "reserved_qty", key: "reserved_qty", render: (v: number) => Number(v || 0).toFixed(0) },
  ];

  const movementCols = [
    { title: "Date", dataIndex: "created_at", key: "created_at", render: (v: string) => dayjs(v).format("DD MMM YYYY HH:mm") },
    { title: "Part", dataIndex: "part_name", key: "part_name" },
    { title: "Type", dataIndex: "movement_type", key: "movement_type", render: (v: string) => <Tag>{v}</Tag> },
    { title: "Qty", dataIndex: "quantity", key: "quantity" },
    { title: "Notes", dataIndex: "notes", key: "notes", render: (v: string) => v || "—" },
  ];

  const catCols = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Description", dataIndex: "description", key: "description", render: (v: string) => v || "—" },
    { title: "", key: "actions", dataIndex: "id", render: (_: any, r: any) => <Popconfirm title="Delete?" onConfirm={() => deleteCat.mutate(r.id)}><Button size="small" danger icon={<Trash2 size={13} />} /></Popconfirm> },
  ];

  const tabItems = [
    {
      key: "suppliers", label: `Suppliers (${suppliers.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Manage suppliers for this company</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setSupModal({ open: true, record: null })}>Add Supplier</Button>
          </div>
          <Table dataSource={suppliers} columns={supplierCols} rowKey="id" loading={supLoading} pagination={{ pageSize: 20 }} scroll={{ x: "max-content" }} size="small" />
        </div>
      ),
    },
    {
      key: "categories", label: `Categories (${categories.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Part categories</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => {
              const name = prompt("Category name:");
              if (name) createCat.mutate({ name });
            }}>Add Category</Button>
          </div>
          <Table dataSource={categories} columns={catCols} rowKey="id" pagination={false} size="small" />
        </div>
      ),
    },
    {
      key: "parts", label: `Parts (${parts.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Parts catalogue</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setPartModal({ open: true, record: null })}>Add Part</Button>
          </div>
          <Table dataSource={parts} columns={partCols} rowKey="id" pagination={{ pageSize: 20 }} scroll={{ x: "max-content" }} size="small" />
        </div>
      ),
    },
    {
      key: "locations", label: `Stock Locations (${locations.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Warehouses, vans, sites</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setLocModal(true)}>Add Location</Button>
          </div>
          <Table dataSource={locations} columns={locationCols} rowKey="id" pagination={false} size="small" />
        </div>
      ),
    },
    {
      key: "levels", label: `Stock Levels (${levels.length})`,
      children: <Table dataSource={levels} columns={levelCols} rowKey="id" pagination={{ pageSize: 50 }} size="small" />,
    },
    {
      key: "movements", label: `Movements (${movements.length})`,
      children: <Table dataSource={movements} columns={movementCols} rowKey="id" pagination={{ pageSize: 20 }} scroll={{ x: "max-content" }} size="small" />,
    },
  ];

  return (
    <div className="p-6">
      {ctx}
      <div className="flex items-center gap-3 mb-6">
        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(-1)} />
        <div>
          <h1 className="text-xl font-semibold text-slate-800 mb-0">Inventory Management</h1>
          <p className="text-slate-500 text-sm">Company ID: {cid}</p>
        </div>
      </div>

      <Tabs items={tabItems} />

      {/* Supplier Modal */}
      <Modal
        title={supModal.record ? "Edit Supplier" : "Add Supplier"}
        open={supModal.open}
        onCancel={() => { setSupModal({ open: false, record: null }); supForm.resetFields(); }}
        footer={null}
        width={600}
      >
        <Form form={supForm} layout="vertical" onFinish={(vals) => {
          if (supModal.record) updateSup.mutate({ ...vals, id: supModal.record.id });
          else createSup.mutate(vals);
        }} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="name" label="Supplier Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="contact_name" label="Contact Name"><Input /></Form.Item>
            <Form.Item name="email" label="Email"><Input type="email" /></Form.Item>
            <Form.Item name="phone" label="Phone"><Input /></Form.Item>
            <Form.Item name="city" label="City"><Input /></Form.Item>
            <Form.Item name="postcode" label="Postcode"><Input /></Form.Item>
            <Form.Item name="payment_terms_days" label="Payment Terms (days)"><InputNumber min={0} className="w-full" /></Form.Item>
            <Form.Item name="vat_number" label="VAT Number"><Input /></Form.Item>
          </div>
          <Form.Item name="address_line1" label="Address"><Input /></Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setSupModal({ open: false, record: null }); supForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createSup.isPending || updateSup.isPending}>Save</Button>
          </div>
        </Form>
      </Modal>

      {/* Part Modal */}
      <Modal
        title={partModal.record ? "Edit Part" : "Add Part"}
        open={partModal.open}
        onCancel={() => { setPartModal({ open: false, record: null }); partForm.resetFields(); }}
        footer={null}
        width={600}
      >
        <Form form={partForm} layout="vertical" onFinish={(vals) => {
          if (partModal.record) updatePart.mutate({ ...vals, id: partModal.record.id });
          else createPart.mutate(vals);
        }} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="name" label="Part Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="sku" label="SKU" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="category_id" label="Category">
              <Select allowClear options={categories.map((c: any) => ({ label: c.name, value: c.id }))} />
            </Form.Item>
            <Form.Item name="supplier_id" label="Supplier">
              <Select allowClear options={suppliers.map((s: any) => ({ label: s.name, value: s.id }))} />
            </Form.Item>
            <Form.Item name="unit" label="Unit"><Input placeholder="each, roll, length…" /></Form.Item>
            <Form.Item name="unit_cost" label="Unit Cost (£)"><InputNumber min={0} step={0.01} className="w-full" /></Form.Item>
            <Form.Item name="unit_price" label="Unit Price (£)"><InputNumber min={0} step={0.01} className="w-full" /></Form.Item>
            <Form.Item name="vat_rate_pct" label="VAT %"><InputNumber min={0} max={100} className="w-full" /></Form.Item>
            <Form.Item name="reorder_point" label="Reorder Point"><InputNumber min={0} className="w-full" /></Form.Item>
            <Form.Item name="reorder_qty" label="Reorder Qty"><InputNumber min={0} className="w-full" /></Form.Item>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setPartModal({ open: false, record: null }); partForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createPart.isPending || updatePart.isPending}>Save</Button>
          </div>
        </Form>
      </Modal>

      {/* Location Modal */}
      <Modal title="Add Stock Location" open={locModal} onCancel={() => { setLocModal(false); locForm.resetFields(); }} footer={null}>
        <Form form={locForm} layout="vertical" onFinish={(vals) => createLoc.mutate(vals)} className="mt-4">
          <Form.Item name="name" label="Location Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="location_type" label="Type" initialValue="warehouse">
            <Select options={[{ label: "Warehouse", value: "warehouse" }, { label: "Van", value: "van" }, { label: "Site", value: "site" }, { label: "Office", value: "office" }]} />
          </Form.Item>
          <Form.Item name="address_line1" label="Address"><Input /></Form.Item>
          <Form.Item name="postcode" label="Postcode"><Input /></Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setLocModal(false); locForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createLoc.isPending}>Save</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
